import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../../firebase';
import { useData, getVideoBaseName } from '../DataProvider';
import { generateBackfilledFilename } from '@appesteem/aeshared-js/loadwatch';
import CropBoxOverlay from '../CropBoxOverlay';

/**
 * ImageCaptureDialog - Dialog for capturing images from video
 * 
 * Props:
 * - open: boolean - controls dialog visibility
 * - onClose: () => void - callback when dialog is closed
 * - appInfo: object - contains clickimagesPath for Firebase storage
 * - videoRef: ref - reference to the video element
 * - currentTime: number - current video time in seconds
 */
const ImageCaptureDialog = ({ open, onClose, appInfo, videoRef, currentTime: initialTime }) => {
  const { allRows } = useData();
  
  // Capture mode: 'full' or 'custom'
  const [captureMode, setCaptureMode] = useState('full');
  
  // Timestamp for capture (in seconds)
  const [captureTime, setCaptureTime] = useState(initialTime || 0);
  
  // Crop box for custom mode (relative to preview canvas)
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  // Preview state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  
  // UI state
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Refs
  const previewCanvasRef = useRef(null);
  const previewContainerRef = useRef(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setCaptureTime(initialTime || 0);
      setPreviewUrl(null);
      setCapturedBlob(null);
      setError(null);
      setSuccess(false);
      setCaptureMode('full');
      setCropBox({ x: 0, y: 0, width: 0, height: 0 });
    }
  }, [open, initialTime]);

  // Get video dimensions
  const getVideoDimensions = useCallback(() => {
    if (!videoRef?.current) return { width: 640, height: 480 };
    return {
      width: videoRef.current.videoWidth || 640,
      height: videoRef.current.videoHeight || 480,
    };
  }, [videoRef]);

  // Capture frame from video at specified time
  const captureFrame = useCallback(async () => {
    const video = videoRef?.current;
    if (!video) {
      setError('Video not available');
      return;
    }

    setError(null);
    
    try {
      // Seek video to capture time
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Seek timeout')), 5000);
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          clearTimeout(timeout);
          resolve();
        };
        video.addEventListener('seeked', onSeeked);
        video.currentTime = captureTime;
      });

      const { width, height } = getVideoDimensions();
      
      // For full mode, capture the entire frame and set preview
      if (captureMode === 'full') {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, width, height);

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error('Failed to create image blob'));
          }, 'image/jpeg', 0.95);
        });
        
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setCapturedBlob(blob);
      }
      
      // For custom mode, draw the full frame to canvas for region selection
      // (preview is generated when user clicks "Apply Crop")
      if (captureMode === 'custom' && previewCanvasRef.current) {
        const previewCtx = previewCanvasRef.current.getContext('2d');
        previewCanvasRef.current.width = width;
        previewCanvasRef.current.height = height;
        previewCtx.drawImage(video, 0, 0, width, height);
        
        // Initialize crop box with an inset from edges
        if (cropBox.width === 0) {
          const inset = 200;
          const canvasW = previewCanvasRef.current.width;
          const canvasH = previewCanvasRef.current.height;
          setCropBox({ 
            x: inset, 
            y: inset, 
            width: Math.max(canvasW - inset * 2, 50), 
            height: Math.max(canvasH - inset * 2, 50) 
          });
        }
      }
      
    } catch (err) {
      setError(err.message || 'Failed to capture frame');
    }
  }, [videoRef, captureTime, captureMode, getVideoDimensions, cropBox.width]);

  // Re-capture when mode or time changes
  useEffect(() => {
    if (open && videoRef?.current) {
      captureFrame();
    }
  }, [open, captureMode, captureTime, captureFrame, videoRef]);

  // Upload captured image to Firebase
  const handleUpload = async () => {
    if (!capturedBlob) {
      setError('No image captured');
      return;
    }

    if (!appInfo?.clickimagesPath) {
      setError('Click images path not configured');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Calculate absolute capture time from video offset and recording start
      let captureDateTime;
      const videoOffsetMs = captureTime * 1000;
      
      // Try to get recording start time from allRows
      if (allRows && allRows.length > 0) {
        const firstEvent = allRows.find(row => row.ActionTime);
        if (firstEvent) {
          const actionDate = new Date(firstEvent.ActionTime);
          const videoOffsetAtEvent = firstEvent.VideoOffset || 0;
          const recordingStartTime = actionDate.getTime() - videoOffsetAtEvent;
          captureDateTime = new Date(recordingStartTime + videoOffsetMs);
        }
      }
      
      // Fallback to current time if we can't calculate
      if (!captureDateTime) {
        captureDateTime = new Date();
      }
      
      // Use the exported generateBackfilledFilename function
      const filename = generateBackfilledFilename(captureDateTime.toISOString());
      if (!filename) {
        setError('Failed to generate filename');
        setUploading(false);
        return;
      }
      
      // Add 'm' suffix to indicate manual capture (avoids collision with auto-generated)
      const manualFilename = `${filename}m`;
      const filePath = `${appInfo.clickimagesPath}/${manualFilename}.jpg`;
      
      console.log('Uploading click image to:', filePath);
      
      const fileRef = ref(storage, filePath);
      await uploadBytes(fileRef, capturedBlob, { contentType: 'image/jpeg' });
      
      console.log('Click image uploaded successfully to:', filePath);

      setUploading(false);
      setSuccess(true);
      
      // Auto-close after showing success
      setTimeout(() => {
        onClose();
      }, 1200);
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
      setUploading(false);
    }
  };

  // Format time for display
  const formatTime = (sec) => {
    if (!sec || isNaN(sec)) return '0:00.000';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    const ms = Math.round((sec % 1) * 1000);
    return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  };

  // Format time for filename (no special characters)
  const formatTimeForFilename = (sec) => {
    if (!sec || isNaN(sec)) return '0m00s000';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    const ms = Math.round((sec % 1) * 1000);
    return `${m}m${s.toString().padStart(2, '0')}s${ms.toString().padStart(3, '0')}`;
  };

  // Generate download filename based on video source path and timestamp
  const getDownloadFilename = () => {
    const baseName = getVideoBaseName(videoRef?.current?.src);
    return `${baseName}_${formatTimeForFilename(captureTime)}.png`;
  };

  // Scale crop box from canvas coordinates to display coordinates for CropBoxOverlay
  const scaledCropBox = React.useMemo(() => {
    const canvas = previewCanvasRef.current;
    if (!canvas || cropBox.width === 0) return cropBox;
    
    // Use canvas bounding rect (not container) to get accurate display dimensions
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvasRect.width / canvas.width;
    const scaleY = canvasRect.height / canvas.height;
    
    return {
      x: cropBox.x * scaleX,
      y: cropBox.y * scaleY,
      width: cropBox.width * scaleX,
      height: cropBox.height * scaleY,
    };
  }, [cropBox]);

  // Handle crop box changes from CropBoxOverlay (convert back to canvas coordinates)
  const handleScaledCropBoxChange = useCallback((newScaledBox) => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    
    // Use canvas bounding rect (same as scaledCropBox) for consistency
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / canvasRect.width;
    const scaleY = canvas.height / canvasRect.height;
    
    setCropBox({
      x: Math.round(newScaledBox.x * scaleX),
      y: Math.round(newScaledBox.y * scaleY),
      width: Math.round(newScaledBox.width * scaleX),
      height: Math.round(newScaledBox.height * scaleY),
    });
  }, []);

  // Apply crop - capture the selected region (called manually by button)
  const applyCrop = useCallback(() => {
    if (captureMode !== 'custom' || cropBox.width <= 0 || cropBox.height <= 0) return;
    if (!previewCanvasRef.current || previewCanvasRef.current.width <= 0) return;
    
    const video = videoRef?.current;
    if (!video) return;

    const { width, height } = getVideoDimensions();
    if (width <= 0 || height <= 0) return;
    
    const scaleX = width / previewCanvasRef.current.width;
    const scaleY = height / previewCanvasRef.current.height;
    
    const cropW = Math.max(1, Math.round(cropBox.width * scaleX));
    const cropH = Math.max(1, Math.round(cropBox.height * scaleY));
    
    const canvas = document.createElement('canvas');
    canvas.width = cropW;
    canvas.height = cropH;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      video,
      Math.round(cropBox.x * scaleX), Math.round(cropBox.y * scaleY), cropW, cropH,
      0, 0, cropW, cropH
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setCapturedBlob(blob);
      }
    }, 'image/jpeg', 0.95);
  }, [cropBox, captureMode, videoRef, getVideoDimensions]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Capture Image</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ position: 'relative' }}>
        {/* Capture Mode & Timestamp Row */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <ToggleButtonGroup
            value={captureMode}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setCaptureMode(value);
                // Clear preview when switching to custom mode (user must click Apply Crop)
                if (value === 'custom') {
                  setPreviewUrl(null);
                  setCapturedBlob(null);
                }
              }
            }}
            size="small"
          >
            <ToggleButton value="full">
              <Tooltip title="Capture entire video frame">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FullscreenIcon fontSize="small" />
                  <span>Full Frame</span>
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="custom">
              <Tooltip title="Select a custom region to capture">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CropIcon fontSize="small" />
                  <span>Custom Region</span>
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Timestamp:</Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace',
                bgcolor: 'action.hover',
                px: 1,
                py: 0.5,
                borderRadius: 1
              }}
            >
              {formatTime(captureTime)}
            </Typography>
          </Box>
        </Box>

        {/* Preview Area */}
        {captureMode === 'full' ? (
          /* Full Frame Mode - show captured preview */
          <Box>
            <Typography variant="subtitle2" gutterBottom>Captured Image</Typography>
            <Box 
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 1,
                minHeight: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default'
              }}
            >
              {previewUrl ? (
                <img 
                  src={previewUrl} 
                  alt="Captured preview" 
                  style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
                />
              ) : (
                <Typography color="text.secondary">
                  Capturing frame...
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          /* Custom Region Mode - show frame with crop overlay, then preview below */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Frame with crop overlay */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">
                  Select Region (drag to move, corners to resize)
                </Typography>
                <Button 
                  size="small" 
                  variant="contained"
                  onClick={applyCrop}
                  disabled={cropBox.width <= 0}
                  startIcon={<CropIcon />}
                >
                  Apply Crop
                </Button>
              </Box>
              <Box 
                ref={previewContainerRef}
                sx={{ 
                  position: 'relative', 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 1, 
                  overflow: 'hidden' 
                }}
              >
                <canvas
                  ref={previewCanvasRef}
                  style={{ 
                    width: '100%', 
                    height: 'auto',
                    display: 'block'
                  }}
                />
                {/* Use the existing CropBoxOverlay component */}
                {cropBox.width > 0 && previewContainerRef.current && (
                  <CropBoxOverlay
                    cropBox={scaledCropBox}
                    onCropBoxChange={handleScaledCropBoxChange}
                    videoRef={previewContainerRef}
                  />
                )}
              </Box>
            </Box>

            {/* Cropped preview (shown after Apply Crop is clicked) */}
            {previewUrl && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Cropped Preview</Typography>
                <Box 
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1, 
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default'
                  }}
                >
                  <img 
                    src={previewUrl} 
                    alt="Cropped preview" 
                    style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Adjust the region above and click "Apply Crop" to update
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Status Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
        
        {/* Success Overlay */}
        {success && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.95)',
              zIndex: 100,
              borderRadius: 1,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main">
              Upload Complete!
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={uploading || success}>
          Cancel
        </Button>
        <Button
          component="a"
          href={previewUrl || '#'}
          download={getDownloadFilename()}
          variant="outlined"
          disabled={!previewUrl || uploading || success}
        >
          Download
        </Button>
        {appInfo?.clickimagesPath && (
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!capturedBlob || uploading || success}
            startIcon={uploading ? <CircularProgress size={16} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload as Click Image'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImageCaptureDialog;
